import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { services } from './data/services';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ReactLenis } from '@studio-freight/react-lenis';

import Header from './header.js';
import Hero from './components/Hero';

// Below-the-fold components — lazy loaded ώστε να μη βαραίνουν το αρχικό JS bundle
const CustomCursor       = lazy(() => import('./components/CustomCursor'));
const TextRevealSection  = lazy(() => import('./components/TextRevealSection'));
const ServicesSection    = lazy(() => import('./components/ServicesSection'));
const ToursArchive       = lazy(() => import('./pages/ToursArchive'));
const WhyPartnerSection  = lazy(() => import('./components/WhyPartnerSection'));
const MembersSection     = lazy(() => import('./components/MembersSection'));
const CrossPromoSection  = lazy(() => import('./components/CrossPromoSection'));
const Footer             = lazy(() => import('./components/Footer'));

// Lazy loaded core pages
const AdminToursDashboard     = lazy(() => import('./pages/AdminToursDashboard'));
const AdminTourForm           = lazy(() => import('./pages/AdminTourForm'));
const AdminLogin              = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard          = lazy(() => import('./pages/AdminDashboard'));
const AdminPackageForm        = lazy(() => import('./pages/AdminPackageForm'));
const About                   = lazy(() => import('./pages/About'));
const Contact                 = lazy(() => import('./pages/Contact'));
const PackagesArchive         = lazy(() => import('./pages/PackagesArchive'));
const PackagePage             = lazy(() => import('./pages/PackagePage'));  // Dynamic route
const TravelPackagesDownloads = lazy(() => import('./pages/TravelPackagesDownloads'));
const ServicePage             = lazy(() => import('./services/ServicePage')); // Dynamic route

gsap.registerPlugin(ScrollTrigger);

// Με client-side navigation (<Link>) η σελίδα δεν ξαναφορτώνει, οπότε
// γυρνάμε πάνω-πάνω σε κάθε αλλαγή route (εκτός αν υπάρχει #anchor).
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
    ScrollTrigger.refresh();
  }, [pathname, hash]);
  return null;
};

const MobileHeroTransition = () => (
  <div
    className="md:hidden w-full"
    style={{
      height: '25vh',
      background: 'linear-gradient(to bottom, transparent 0%, rgb(249,245,233) 100%)',
      marginTop: '-25vh',
      position: 'relative',
      zIndex: 5,
      pointerEvents: 'none',
    }}
  />
);

const HomePage = ({ language, setLanguage }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Header language={language} setLanguage={setLanguage} />
      <ReactLenis root options={{ lerp: 0.1, duration: 1.5, smoothWheel: true }}>
        <div 
          className="min-h-screen overflow-x-hidden transition-opacity duration-500"
          style={{ backgroundColor: 'rgb(249, 245, 233)', opacity: isLoaded ? 1 : 0 }}
        >
          <div className="fixed top-0 left-0 w-screen h-screen pointer-events-none z-[9999]">
            <div className="hidden md:grid h-full w-full max-w-[1920px] mx-auto grid-cols-12 gap-0">
              {[...Array(12)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-full pointer-events-none" 
                  style={{ 
                    borderRightWidth: '1px', 
                    borderLeftWidth: i === 0 ? '1px' : '0px', 
                    borderRightColor: i === 11 ? 'rgba(7, 29, 61, 0.15)' : 'rgba(7, 29, 61, 0.04)', 
                    borderLeftColor: i === 0 ? 'rgba(7, 29, 61, 0.15)' : 'transparent' 
                  }} 
                />
              ))}
            </div>
            <div className="md:hidden grid h-full w-full grid-cols-5 gap-0">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-full pointer-events-none" 
                  style={{ 
                    borderRightWidth: '1px', 
                    borderLeftWidth: i === 0 ? '1px' : '0px', 
                    borderRightColor: i === 4 ? 'rgba(7, 29, 61, 0.12)' : 'rgba(7, 29, 61, 0.04)', 
                    borderLeftColor: i === 0 ? 'rgba(7, 29, 61, 0.12)' : 'transparent' 
                  }} 
                />
              ))}
            </div>
          </div>

          <Hero />
          <MobileHeroTransition />
          <Suspense fallback={null}>
            <CustomCursor />
            <TextRevealSection />
            <ServicesSection services={services} />
            <WhyPartnerSection />
            <MembersSection />
            <CrossPromoSection />
            <Footer />
          </Suspense>
        </div>
      </ReactLenis>
    </>
  );
};

const App = () => {
  const [language, setLanguage] = useState('en');

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'rgb(249, 245, 233)' }}>
          <div className="w-px h-16 bg-[#071d3d]/30 animate-pulse" />
        </div>
      }>
        <Routes>
          {/* Main Routes */}
          <Route path="/" element={<HomePage language={language} setLanguage={setLanguage} />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/tours" element={<ToursArchive />} />

          {/* Packages */}
          <Route path="/packages" element={<PackagesArchive language={language} setLanguage={setLanguage} />} />
          <Route path="/packages/:slug" element={<PackagePage />} />
          <Route path="/travel-packages" element={<TravelPackagesDownloads />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/packages/new" element={<AdminPackageForm />} />
          <Route path="/admin/packages/edit/:slug" element={<AdminPackageForm />} />
          <Route path="/admin/tours" element={<AdminToursDashboard />} />
          <Route path="/admin/tours/new" element={<AdminTourForm />} />
          <Route path="/admin/tours/edit/:slug" element={<AdminTourForm />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;